import { Boxes, HelpCircle, Info, ListOrdered, Package, WifiOff, Wifi, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/Tabs';
import { ThemeToggle } from './components/ThemeToggle';
import { AboutDialog } from './components/AboutDialog';
import { CiscoLogo } from './components/CiscoLogo';
import { OnboardingGuide, hasSeenOnboarding } from './components/OnboardingGuide';
import { RoomsPage } from './features/rooms/RoomsPage';
import { SequencesPage } from './features/sequences/SequencesPage';
import { ModelsPage } from './features/models/ModelsPage';
import { UsersPage } from './features/users/UsersPage';
import { AudiencePage } from './features/rooms/AudiencePage';
import { AuthProvider, useAuth } from './features/auth/AuthContext';
import { AuthGate } from './features/auth/AuthGate';
import { UserBadge } from './features/auth/UserBadge';
import { getSocket } from './lib/socket';
import casperLogo from './assets/casperLogoIcon.png';
import cilLogo from './assets/CILLogo.png';

const UID_RE = /^[A-Za-z0-9]{12}$/;

export function App() {
  // Audience view is a projector-only render of public room info (QR + marker).
  // It's opened by a signed-in presenter in a new window and doesn't need auth.
  const params = new URLSearchParams(window.location.search);
  const audienceId = params.get('audience');
  if (audienceId && UID_RE.test(audienceId)) {
    // Rotated (vertical-marker) mode is the default for the audience QR —
    // opt out with ?rotation=flat.
    return <AudiencePage roomId={audienceId} rotated={params.get('rotation') !== 'flat'} />;
  }

  return (
    <AuthProvider>
      <AuthGate>
        <AppInner />
      </AuthGate>
    </AuthProvider>
  );
}

function AppInner() {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  useEffect(() => {
    if (!hasSeenOnboarding()) setOnboardingOpen(true);
  }, []);

  useEffect(() => {
    let mounted = true;
    getSocket().then((s) => {
      if (!mounted) return;
      const onConn = () => setConnected(true);
      const onDisc = () => setConnected(false);
      s.on('connect', onConn);
      s.on('disconnect', onDisc);
      setConnected(s.connected);
      return () => {
        s.off('connect', onConn);
        s.off('disconnect', onDisc);
      };
    });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-30 border-b bg-card/80 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4">
          <div className="flex items-center gap-3 shrink-0">
            <CiscoLogo className="h-9 w-auto" />
            <div className="h-6 w-px bg-border" />
            <img
              src={cilLogo}
              alt="Cisco Innovation Labs"
              className="h-8 w-auto rounded-sm bg-white p-1 dark:bg-white"
            />
            <img src={casperLogo} alt="cAspeR" className="h-9 w-auto" />
          </div>
          <div className="flex items-center gap-2">
            <div className="flex flex-col leading-none">
              <span className="text-sm font-semibold tracking-tight">cAspeR</span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Control UI
              </span>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {connected ? (
                <>
                  <Wifi className="h-3.5 w-3.5 text-emerald-500" />
                  <span>Connected</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3.5 w-3.5 text-destructive" />
                  <span>Disconnected</span>
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => setOnboardingOpen(true)}
              aria-label="How cAspeR works"
              title="How cAspeR works"
              className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setAboutOpen(true)}
              aria-label="About cAspeR"
              title="About cAspeR"
              className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <Info className="h-4 w-4" />
            </button>
            <ThemeToggle />
            <UserBadge />
          </div>
        </div>
      </header>

      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
      <OnboardingGuide open={onboardingOpen} onOpenChange={setOnboardingOpen} />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6">
        <Tabs defaultValue="rooms" className="w-full">
          <TabsList>
            <TabsTrigger value="rooms" className="gap-2">
              <Boxes className="h-4 w-4" />
              Rooms
            </TabsTrigger>
            <TabsTrigger value="sequences" className="gap-2">
              <ListOrdered className="h-4 w-4" />
              Sequences
            </TabsTrigger>
            <TabsTrigger value="models" className="gap-2">
              <Package className="h-4 w-4" />
              Models
            </TabsTrigger>
            {user?.role === 'admin' && (
              <TabsTrigger value="users" className="gap-2">
                <Users className="h-4 w-4" />
                Users
              </TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="rooms">
            <RoomsPage />
          </TabsContent>
          <TabsContent value="sequences">
            <SequencesPage />
          </TabsContent>
          <TabsContent value="models">
            <ModelsPage />
          </TabsContent>
          {user?.role === 'admin' && (
            <TabsContent value="users">
              <UsersPage />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
